import React, {useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {JobHazardAnalysisViewType} from 'src/shared/constants/ViewTypes';

import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {v4 as uuidv4} from 'uuid';

import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import type {JobHazardAnalysisStepResponse} from 'src/api/ProactApiClient';
import {
  EntityIdentifierEnum,
  FeatureFlagEnum,
  JobHazardAnalysisStateEnum,
  RiskAssessmentCodeAreaEnum,
} from 'src/api/ProactApiClient';
import {
  useGetJobHazardAnalysisQuery,
  useTransitionJobHazardAnalysisMutation,
} from 'src/redux/queries/jobHazardAnalysis';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {SectionCard} from 'src/design/atoms/SectionCard';
import type {PageNavigationConfig} from 'src/app/layout/pageNavigationConfig';
import {buildPageNavigation} from 'src/app/layout/pageNavigationConfig';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {SidebarLinkJobHazardAnalysis} from 'src/app/layout/Sidebar/SidebarLinksConfig';

import {AuditModule} from 'src/app/audit-logs/AuditModule';
import {JobHazardAnalysisFormView} from 'src/app/job-hazard-analysis/JobHazardAnalysisFormView';
import {
  JobHazardAnalysisPillConfig,
  jhaDocumentTitle,
  pageTitleDict,
} from 'src/app/job-hazard-analysis/jobHazardAnalysisHelpers';
import {JobHazardAnalysisStepList} from 'src/app/job-hazard-analysis/JobHazardAnalysisStepList';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useGetRiskAssessmentCodeByAreaQuery} from 'src/redux/queries/riskAsessmentCode';
import {useGetJobHazardAnalysisStateMachineQuery} from 'src/redux/queries/jobHazardAnalysis';
import type {ContextualEntity} from 'src/shared/auth/permissions/contextualTypes';
import {useAuditLogPermissions} from 'src/shared/auth/permissions/useAuditLogPermissions';
import {useContextualPermissionEvaluator} from 'src/shared/auth/permissions/useContextualPermission';
import {useJHAPermissions} from 'src/shared/auth/permissions/useJHAPermissions';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const navigationConfig: PageNavigationConfig = {
  entityName: pageTitleDict.singular,
  entityNamePlural: pageTitleDict.plural,
  listPath: PathUtility.JobHazardAnalysis(),
  viewPath: id => PathUtility.JobHazardAnalysisView(String(id), 'content'),
};

export const ViewJobHazardAnalysisPage: React.FC = () => {
  const navigate = useAppNavigate();
  const {id, view} = useParams();
  const auditPerms = useAuditLogPermissions();
  const jhaPerms = useJHAPermissions();
  const {canPerform} = useContextualPermissionEvaluator();

  const [jobStepsList, setJobStepsList] = useState<
    JobHazardAnalysisStepResponse[]
  >([]);
  const [transition, setTransition] = useState<string | null>(null);
  const [confirmTransition, setConfirmTransition] = useState<string | null>(
    null
  );

  const {setAlert} = useAlert();

  useSetDocumentTitle(jhaDocumentTitle);

  const {data: riskAsset} = useGetRiskAssessmentCodeByAreaQuery(
    RiskAssessmentCodeAreaEnum.Safety
  );

  const {data: stateMachineData} = useGetJobHazardAnalysisStateMachineQuery();

  const workflowId = parseInt(id ?? '-1');

  const {
    data: item,
    isLoading,
    error: fetchError,
  } = useGetJobHazardAnalysisQuery(workflowId, {skip: workflowId < 0});

  const [transitionJha] = useTransitionJobHazardAnalysisMutation();

  const loadError = fetchError ? parseErrorMessage(fetchError) : undefined;

  useEffect(() => {
    if (item) {
      const steps = item.jobHazardAnalysisSteps.map(step => ({
        ...step,
        offlineId: uuidv4(),
      }));
      setJobStepsList(steps);
    }
  }, [item]);

  const navigation = useMemo(
    () =>
      buildPageNavigation(navigationConfig, {
        mode: 'view',
        id,
        itemName: item?.subjectName,
      }),
    [id, item?.subjectName]
  );

  const pills = JobHazardAnalysisPillConfig(item?.state);

  const handleStateMachineTransition = async (targetTransition: string) => {
    setTransition(targetTransition);
    try {
      await transitionJha({
        id: +id!,
        request: {transition: targetTransition},
      }).unwrap();
      setTransition(null);
      setAlert(
        `${
          item?.subjectName ?? pageTitleDict.singular
        } was successfully updated`,
        'success'
      );
    } catch (err) {
      setTransition(null);
      setAlert(parseErrorMessage(err), 'error');
    }
  };

  const contextualEntity = useMemo<ContextualEntity | undefined>(
    () =>
      item
        ? {
            creatorId: item.creatorId,
            creator: {
              id: item.creator?.id,
              supervisorId: item.creator?.supervisorId,
            },
          }
        : undefined,
    [item?.creatorId, item?.creator?.id, item?.creator?.supervisorId]
  );

  // Map from state machine transition name to module-level permission flag.
  // Both the module permission AND the contextual check must pass for a
  // transition button to be visible.
  const transitionPermissionMap: Record<string, boolean> = {
    publish: jhaPerms.transitionPublish,
    manager_approve: jhaPerms.transitionManagerApprove,
    safety_approve: jhaPerms.transitionSafetyApprove,
    unapprove: jhaPerms.transitionUnapprove,
  };

  const availableTransitions = useMemo(() => {
    if (!item || !stateMachineData || !contextualEntity) return [];

    return stateMachineData.transitions.filter(t => {
      if (!t.isVisible) return false;
      if (!t.from.includes(item.state)) return false;
      // Check module-level transition permission (deny unknown transitions by default)
      if (!transitionPermissionMap[t.name]) return false;
      if (
        !canPerform(
          contextualEntity,
          t.contextualPermissions,
          t.requireAllContextualPermissions
        )
      )
        return false;
      return true;
    });
  }, [item, stateMachineData, contextualEntity, canPerform, jhaPerms]);

  const nonDestructiveTransitions = availableTransitions.filter(
    t => t.color !== 'red'
  );
  const destructiveTransitions = availableTransitions.filter(
    t => t.color === 'red'
  );

  const {RenderedTabsComponent} = useUrlQueryTabV2<JobHazardAnalysisViewType>({
    view: (view ?? 'content') as JobHazardAnalysisViewType,
    defaultView: 'content',
    tabs: [
      {
        view: 'content',
        label: 'Content',
        component: (
          <Stack gap="md">
            <JobHazardAnalysisFormView formData={item} />

            <SectionCard title="Job Steps">
              <Box p="md">
                <JobHazardAnalysisStepList
                  steps={jobStepsList}
                  mode="view"
                  riskMatrix={riskAsset}
                  onStepDelete={() => {}}
                  onStepEdit={() => {}}
                />
              </Box>
            </SectionCard>
          </Stack>
        ),
      },
      {
        view: 'audit',
        label: 'Audit Log',
        disabled: !auditPerms.view,
        component: (
          <AuditModule
            entityId={String(id)}
            entityType={EntityIdentifierEnum.JobHazardAnalysis}
          />
        ),
      },
    ],
    onSelectView: view => {
      navigate(PathUtility.JobHazardAnalysisView(id as string, view as any), {
        replace: true,
      });
    },
  });

  if (loadError && !item) {
    return (
      <ContentWrapper featureFlag={FeatureFlagEnum.JobHazardAnalysis}>
        <Stack gap={0}>
          <BackBar
            fallbackTo={navigation.fallbackTo}
            fallbackLabel={navigation.fallbackLabel}
            breadcrumbs={navigation.breadcrumbs}
          />
          <Alert color="red" mt="sm" mx="md">
            {loadError}
          </Alert>
        </Stack>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.JobHazardAnalysis}>
      <Stack gap={0}>
        <BackBar
          fallbackTo={navigation.fallbackTo}
          fallbackLabel={navigation.fallbackLabel}
          breadcrumbs={navigation.breadcrumbs}
        />

        {isLoading ? (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        ) : (
          <>
            <PageHeader>
              <PageHeader.Title icon={SidebarLinkJobHazardAnalysis.icon}>
                {item?.subjectName ?? 'Job Hazard Analysis not found'}
              </PageHeader.Title>
              {pills.length > 0 && (
                <PageHeader.Badges>
                  {pills.map(pill => (
                    <PageHeader.Badge
                      key={pill.text}
                      color={pill.color}
                      variant="light">
                      {pill.text}
                    </PageHeader.Badge>
                  ))}
                </PageHeader.Badges>
              )}
              {item?.internalIdentifier && (
                <PageHeader.Meta>
                  <PageHeader.MetaItem label="ID">
                    {item.internalIdentifier}
                  </PageHeader.MetaItem>
                </PageHeader.Meta>
              )}
              <PageHeader.Actions>
                {nonDestructiveTransitions.map(t => (
                  <PageHeader.Action
                    key={t.name}
                    variant="primary"
                    onClick={() => handleStateMachineTransition(t.name)}
                    loading={transition !== null && transition === t.name}>
                    {t.title}
                  </PageHeader.Action>
                ))}
                {destructiveTransitions.map(t => (
                  <PageHeader.Action
                    key={t.name}
                    variant="danger"
                    onClick={() => setConfirmTransition(t.name)}
                    loading={transition !== null && transition === t.name}>
                    {t.title}
                  </PageHeader.Action>
                ))}
                {item?.state === JobHazardAnalysisStateEnum.Draft &&
                  jhaPerms.update && (
                    <PageHeader.Action
                      variant="edit"
                      onClick={() =>
                        navigate(PathUtility.JobHazardAnalysisEdit(id!))
                      }>
                      Edit
                    </PageHeader.Action>
                  )}
              </PageHeader.Actions>
            </PageHeader>

            {item && <Box>{RenderedTabsComponent}</Box>}

            <ConfirmDialog
              opened={confirmTransition !== null}
              onClose={() => setConfirmTransition(null)}
              onConfirm={() => {
                const target = confirmTransition;
                setConfirmTransition(null);
                if (target) void handleStateMachineTransition(target);
              }}
              title="Confirm Transition"
              body="This will revert the JHA back to Draft status. Are you sure?"
              confirmationButtonColor="red"
              buttonTextYes="Confirm"
              buttonTextNo="Cancel"
            />
          </>
        )}
      </Stack>
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.JobHazardAnalysisView(':id', ':view'),
  element: <ViewJobHazardAnalysisPage />,
  isPrivate: true,
});
